<script lang="ts">
	import { onMount } from 'svelte';
	import { goto } from '$app/navigation';
	import { t } from '#lib/i18n/state.svelte';
	import { fetchUser, authState } from '#lib/auth/store.svelte';
	import LangToggle from '#lib/components/LangToggle.svelte';

	let error = $state('');

	onMount(async () => {
		const params = new URLSearchParams(window.location.search);
		error = params.has('error') ? t('auth.error.generic') : '';
		const user = await fetchUser();
		if (user) await goto(authState().access.allowed ? '/app' : '/auth/access');
	});

</script>

<svelte:head>
	<title>{t('auth.title')} — RepCheck</title>
</svelte:head>

<div class="wrap">
	<header class="top">
		<a class="brand" href="/">
			<span class="logo" aria-hidden="true">R<span class="dot"></span></span>
			<span>RepCheck</span>
		</a>
		<LangToggle compact />
	</header>

	<main class="card">
		<p class="eyebrow">{t('auth.eyebrow')}</p>
		<h1>{t('auth.title')}</h1>
		<p class="lede">{t('auth.subtitle')}</p>

		{#if error}
			<p class="error" role="alert">{error}</p>
		{/if}

		<div class="providers">
			<a class="btn btn-primary provider" href="/api/auth/login/libedge">
				<span class="pmark">L</span> {t('auth.libedge')}
			</a>
			<a class="btn provider" href="/api/auth/login/google"><span class="pmark">G</span> {t('auth.google')}</a>
		</div>

		<p class="note">{t('auth.note')}</p>
	</main>
</div>

<style>
	.wrap {
		min-height: 100vh;
		display: grid;
		grid-template-rows: auto 1fr;
		background: var(--paper);
	}
	.top {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.9rem clamp(1rem, 3vw, 2rem);
		border-bottom: 1px solid var(--line);
	}
	.brand {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		text-decoration: none;
		color: var(--ink);
		font-weight: 650;
	}
	.logo {
		position: relative;
		display: grid;
		place-items: center;
		width: 30px;
		height: 30px;
		border-radius: 8px;
		background: var(--ink);
		color: var(--paper);
		font-family: var(--font-display);
		font-style: italic;
	}
	.dot {
		position: absolute;
		right: 5px;
		bottom: 5px;
		width: 4px;
		height: 4px;
		border-radius: 50%;
		background: var(--accent);
	}
	.card {
		width: min(28rem, calc(100% - 2rem));
		margin: 3rem auto;
		padding: 1.5rem;
		border: 1px solid var(--line);
		border-radius: var(--radius);
		background: #fff;
		box-shadow: var(--shadow-sm);
		align-self: start;
	}
	.eyebrow {
		margin: 0 0 0.3rem;
		font-size: 0.68rem;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--muted);
		font-weight: 650;
	}
	h1 {
		font-size: 1.5rem;
		margin: 0 0 0.4rem;
	}
	.lede {
		margin: 0 0 1rem;
		color: var(--ink-2);
		font-size: 0.9rem;
	}
	.providers {
		display: grid;
		gap: 0.5rem;
	}
	.provider {
		justify-content: center;
		text-decoration: none;
	}
	.pmark {
		display: inline-grid;
		place-items: center;
		width: 22px;
		height: 22px;
		margin-right: 0.4rem;
		border-radius: 5px;
		background: rgb(255 255 255 / 0.2);
		font-size: 0.7rem;
		font-weight: 700;
	}
	.note {
		margin: 1rem 0 0;
		font-size: 0.75rem;
		color: var(--muted);
	}
	.error {
		color: var(--critical);
		background: var(--critical-soft);
		border: 1px solid #f2c9c4;
		border-radius: 8px;
		padding: 0.5rem 0.7rem;
		font-size: 0.82rem;
	}
</style>
